import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin, message } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import Layout from '@/components/layout/Layout';
import AuthForm from '@/components/auth/AuthForm';
import Dashboard from '@/pages/Dashboard';
import DataInput from '@/pages/DataInput';
import DataList from '@/pages/DataList';
import Charts from '@/pages/Charts';
import Export from '@/pages/Export';
import AIAssistant from '@/pages/AIAssistant';
import { useHealthStore } from '@/stores/useHealthStore';
import { getCurrentUser, signOut } from '@/services/supabase';
import { clearMemoryCache } from '@/services/cloudStore';

const App: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const loadRecords = useHealthStore(state => state.loadRecords);
  const clearData = useHealthStore(state => state.clearData);

  // 检查登录状态
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await getCurrentUser();
        if (user) {
          setIsLoggedIn(true);
          setUserEmail('email' in user ? user.email || null : null);
          setIsAnonymous(user.isAnonymous || false);

          // 自动从云端拉取数据
          setIsSyncing(true);
          try {
            await loadRecords();
            message.success('数据已从云端同步');
          } catch (error) {
            console.error('Sync error:', error);
            message.error('同步数据失败，请检查网络');
          } finally {
            setIsSyncing(false);
          }
        } else {
          setIsLoggedIn(false);
        }
      } catch (error) {
        console.error('Auth check error:', error);
        setIsLoggedIn(false);
      }
    };

    checkAuth();
  }, [loadRecords]);

  const handleLoginSuccess = async () => {
    const user = await getCurrentUser();
    if (user) {
      setIsLoggedIn(true);
      setUserEmail('email' in user ? user.email || null : null);
      setIsAnonymous(user.isAnonymous || false);

      // 登录后自动从云端拉取数据
      setIsSyncing(true);
      try {
        await loadRecords();
        message.success('登录成功，数据已同步');
      } catch (error) {
        console.error('Login sync error:', error);
        message.error('数据同步失败');
      } finally {
        setIsSyncing(false);
      }
    }
  };

  const handleLogout = async () => {
    await signOut();
    clearMemoryCache();
    clearData();
    setIsLoggedIn(false);
    setUserEmail(null);
    setIsAnonymous(false);
  };

  // 显示加载状态
  if (isLoggedIn === null || isSyncing) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: 16
      }}>
        <Spin size="large" />
        <div>{isSyncing ? '正在从云端同步数据...' : '加载中...'}</div>
      </div>
    );
  }

  // 未登录显示登录页面
  if (!isLoggedIn) {
    return (
      <ConfigProvider locale={zhCN} theme={{ token: { colorPrimary: '#1890ff' } }}>
        <AuthForm onLoginSuccess={handleLoginSuccess} />
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider locale={zhCN} theme={{ token: { colorPrimary: '#1890ff' } }}>
      <BrowserRouter>
        <Layout
          userEmail={userEmail}
          isAnonymous={isAnonymous}
          onLogout={handleLogout}
        >
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/input" element={<DataInput />} />
            <Route path="/list" element={<DataList />} />
            <Route path="/charts" element={<Charts />} />
            <Route path="/export" element={<Export />} />
            <Route path="/ai" element={<AIAssistant />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </ConfigProvider>
  );
};

export default App;